import { Container, Sprite, Graphics, Text, TextStyle } from 'pixi.js';
import { Assets } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, ANIMATION, FONTS } from '../constants/theme';
import type { WarriorInstance } from '@/types';
import { ElectricAura } from './ElectricAura';

export interface WarriorSpriteOptions {
  warrior: WarriorInstance;
  tileSize: number;
  isPlayer2: boolean;
  onAttack?: () => void;
  onHeal?: () => void;
}

/**
 * Draw a sword icon for attack button
 */
function drawSwordIcon(graphics: Graphics, size: number, color: number): void {
  const half = size / 2;
  // Blade (triangle)
  graphics.moveTo(0, -half);
  graphics.lineTo(half * 0.3, half * 0.4);
  graphics.lineTo(-half * 0.3, half * 0.4);
  graphics.closePath();
  graphics.fill({ color });
  // Handle (rectangle)
  graphics.rect(-half * 0.15, half * 0.4, half * 0.3, half * 0.4);
  graphics.fill({ color });
  // Crossguard
  graphics.rect(-half * 0.4, half * 0.3, half * 0.8, half * 0.15);
  graphics.fill({ color });
}

/**
 * Draw a heart icon for heal button
 */
function drawHeartIcon(graphics: Graphics, size: number, color: number): void {
  const half = size / 2;
  // Two circles on top
  graphics.circle(-half * 0.3, -half * 0.2, half * 0.4);
  graphics.fill({ color });
  graphics.circle(half * 0.3, -half * 0.2, half * 0.4);
  graphics.fill({ color });
  // Triangle at bottom
  graphics.moveTo(-half * 0.65, -half * 0.1);
  graphics.lineTo(0, half * 0.7);
  graphics.lineTo(half * 0.65, -half * 0.1);
  graphics.closePath();
  graphics.fill({ color });
}

/**
 * Warrior sprite displayed on the board
 */
export class WarriorSprite extends Container {
  private sprite: Sprite | null = null;
  private placeholder: Graphics;
  private electricAura: ElectricAura | null = null;
  private attackReadyIndicator: Graphics;
  private healReadyIndicator: Graphics;
  private healthBarBg: Graphics;
  private healthBarFill: Graphics;
  private healthBarContainer: Container;
  private indicatorContainer: Container;
  private actionButtonsContainer: Container;
  private attackButton: Container | null = null;
  private healButton: Container | null = null;
  private options: WarriorSpriteOptions;
  private isSelected = false;
  private canAttack = false;
  private canHeal = false;
  private attackCooldown = 0;
  private attackFrequency = 0;
  private healCooldown = 0;
  private healFrequency = 0;
  private healingPower = 0;
  private pulseAnimation: gsap.core.Tween | null = null;
  private counterPulseAnimation: gsap.core.Tween | null = null;
  private indicatorAnimation: gsap.core.Tween | null = null;

  constructor(options: WarriorSpriteOptions) {
    super();
    this.options = options;

    // Health bar (in a container so it doesn't rotate with sprite) - added first for lowest z-index
    this.healthBarContainer = new Container();
    this.healthBarBg = new Graphics();
    this.healthBarFill = new Graphics();
    this.healthBarContainer.addChild(this.healthBarBg);
    this.healthBarContainer.addChild(this.healthBarFill);
    this.addChild(this.healthBarContainer);

    // Placeholder
    this.placeholder = new Graphics();
    this.addChild(this.placeholder);

    // Readiness indicators container (stays upright)
    this.indicatorContainer = new Container();
    this.indicatorContainer.visible = false;
    this.addChild(this.indicatorContainer);

    // Attack ready indicator (sword icon)
    this.attackReadyIndicator = new Graphics();
    this.attackReadyIndicator.visible = false;
    this.indicatorContainer.addChild(this.attackReadyIndicator);

    // Heal ready indicator (heart/cross icon)
    this.healReadyIndicator = new Graphics();
    this.healReadyIndicator.visible = false;
    this.indicatorContainer.addChild(this.healReadyIndicator);

    // Action buttons container (shown when selected)
    this.actionButtonsContainer = new Container();
    this.actionButtonsContainer.visible = false;
    this.addChild(this.actionButtonsContainer);

    // Store initial cooldown info
    this.attackFrequency = options.warrior.definition.stats.attackFrequency;
    this.healingPower = options.warrior.definition.stats.healingPower;
    this.healFrequency = options.warrior.definition.stats.healingFrequency;
    this.attackCooldown = options.warrior.attackCooldown;
    this.healCooldown = options.warrior.healCooldown;

    this.draw();
    this.drawHealthBar();
    this.drawIndicators();
    this.createActionButtons();
    this.loadSprite();
  }

  private draw(): void {
    const { tileSize, warrior, isPlayer2 } = this.options;
    const spriteSize = tileSize * 0.75;

    // Placeholder
    this.placeholder.clear();
    this.placeholder.circle(0, 0, spriteSize / 2);
    this.placeholder.fill({
      color: warrior.ownerId === 'player1' ? COLORS.PLAYER1 : COLORS.PLAYER2,
    });

    // Rotation for player 2
    if (isPlayer2) {
      this.rotation = Math.PI;
    }

    // Counter-rotate health bar, indicators, and action buttons so they stay upright
    if (isPlayer2) {
      this.healthBarContainer.rotation = -Math.PI;
      this.indicatorContainer.rotation = -Math.PI;
      this.actionButtonsContainer.rotation = -Math.PI;
    }
  }

  private drawIndicators(): void {
    const { tileSize } = this.options;
    const spriteSize = tileSize * 0.75;
    const indicatorSize = 12;
    const indicatorOffset = spriteSize / 2 + 14;

    // Attack indicator (sword shape) - positioned to the left
    this.attackReadyIndicator.clear();
    // Draw a simple sword icon
    this.attackReadyIndicator.moveTo(-3, -indicatorSize / 2);
    this.attackReadyIndicator.lineTo(3, -indicatorSize / 2);
    this.attackReadyIndicator.lineTo(3, indicatorSize / 2 - 3);
    this.attackReadyIndicator.lineTo(6, indicatorSize / 2 - 3);
    this.attackReadyIndicator.lineTo(6, indicatorSize / 2);
    this.attackReadyIndicator.lineTo(-6, indicatorSize / 2);
    this.attackReadyIndicator.lineTo(-6, indicatorSize / 2 - 3);
    this.attackReadyIndicator.lineTo(-3, indicatorSize / 2 - 3);
    this.attackReadyIndicator.closePath();
    this.attackReadyIndicator.fill({ color: COLORS.DANGER });
    this.attackReadyIndicator.stroke({ color: 0xffffff, width: 1 });
    this.attackReadyIndicator.x = -indicatorOffset;
    this.attackReadyIndicator.y = 0;

    // Heal indicator (plus/cross shape) - positioned to the right
    this.healReadyIndicator.clear();
    const crossSize = indicatorSize / 2;
    const crossThickness = 4;
    // Horizontal bar
    this.healReadyIndicator.rect(-crossSize, -crossThickness / 2, crossSize * 2, crossThickness);
    // Vertical bar
    this.healReadyIndicator.rect(-crossThickness / 2, -crossSize, crossThickness, crossSize * 2);
    this.healReadyIndicator.fill({ color: COLORS.SUCCESS });
    this.healReadyIndicator.stroke({ color: 0xffffff, width: 1 });
    this.healReadyIndicator.x = indicatorOffset;
    this.healReadyIndicator.y = 0;
  }

  private drawHealthBar(): void {
    const { tileSize, warrior, isPlayer2 } = this.options;
    const spriteSize = tileSize * 0.75;
    const barWidth = spriteSize * 0.8;
    const barHeight = 4;
    const barOffset = spriteSize / 2 + 6;
    // Player 1: health bar below sprite (positive Y)
    // Player 2: health bar above sprite from screen perspective (negative Y, since container is counter-rotated)
    const barY = isPlayer2 ? -(barOffset + barHeight) : barOffset;

    const healthPercent = warrior.currentHealth / warrior.definition.stats.health;

    // Background (dark)
    this.healthBarBg.clear();
    this.healthBarBg.roundRect(-barWidth / 2, barY, barWidth, barHeight, 2);
    this.healthBarBg.fill({ color: 0x1a1a2e, alpha: 0.8 });
    this.healthBarBg.stroke({ color: 0x000000, width: 1, alpha: 0.5 });

    // Fill (colored based on health)
    this.healthBarFill.clear();
    const fillWidth = barWidth * healthPercent;
    if (fillWidth > 0) {
      // Color gradient: green > yellow > red based on health
      let fillColor: number = COLORS.SUCCESS;
      if (healthPercent <= 0.25) {
        fillColor = COLORS.DANGER;
      } else if (healthPercent <= 0.5) {
        fillColor = COLORS.WARNING;
      }

      this.healthBarFill.roundRect(-barWidth / 2, barY, fillWidth, barHeight, 2);
      this.healthBarFill.fill({ color: fillColor });
    }
  }

  private createActionButtons(): void {
    // Clear existing buttons
    this.actionButtonsContainer.removeChildren();
    this.attackButton = null;
    this.healButton = null;

    const { tileSize, isPlayer2 } = this.options;
    const spriteSize = tileSize * 0.75;
    const buttonSize = Math.max(14, tileSize * 0.18); // Small square buttons

    // Position at corners, on same row as health bar
    const healthBarOffset = spriteSize / 2 + 6;
    const xOffset = spriteSize / 2 + buttonSize / 2; // Touch the border of the square
    // Y position aligned with health bar (below sprite for P1, above for P2)
    const yPos = isPlayer2 ? -(healthBarOffset) : healthBarOffset;

    const hasAttack = this.attackFrequency > 0 || this.options.warrior.definition.stats.damage > 0;
    const hasHeal = this.healingPower > 0;

    // Attack button (left side, overlapping corner)
    if (hasAttack) {
      this.attackButton = this.createActionButton(
        'attack',
        buttonSize,
        -xOffset,
        yPos,
        isPlayer2
      );
      this.actionButtonsContainer.addChild(this.attackButton);
    }

    // Heal button (right side, overlapping corner)
    if (hasHeal) {
      this.healButton = this.createActionButton(
        'heal',
        buttonSize,
        xOffset,
        yPos,
        isPlayer2
      );
      this.actionButtonsContainer.addChild(this.healButton);
    }

    this.updateActionButtons();
  }

  private createActionButton(
    type: 'attack' | 'heal',
    size: number,
    x: number,
    y: number,
    isPlayer2: boolean
  ): Container {
    const button = new Container();
    button.x = x;
    button.y = y;

    // Background graphics
    const bg = new Graphics();
    button.addChild(bg);

    // Content container (rotated for player 2)
    const contentContainer = new Container();
    if (isPlayer2) {
      contentContainer.rotation = Math.PI; // Rotate content 180 degrees for player 2
    }
    button.addChild(contentContainer);

    // Icon/text container
    const iconContainer = new Graphics();
    contentContainer.addChild(iconContainer);

    // Counter text (shown when charging)
    const textStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: Math.max(8, size * 0.4),
      fontWeight: 'bold',
      fill: 0xffffff,
    });
    const counterText = new Text({ text: '', style: textStyle });
    counterText.anchor.set(0.5);
    contentContainer.addChild(counterText);

    // Store references
    (button as any).bg = bg;
    (button as any).iconContainer = iconContainer;
    (button as any).counterText = counterText;
    (button as any).type = type;
    (button as any).size = size;

    // Interaction
    button.eventMode = 'static';
    button.cursor = 'default';

    button.on('pointerdown', (e) => {
      e.stopPropagation();
      if (!this.isSelected) return;

      if (type === 'attack' && this.canAttack && this.options.onAttack) {
        this.options.onAttack();
      } else if (type === 'heal' && this.canHeal && this.options.onHeal) {
        this.options.onHeal();
      }
    });

    button.on('pointerover', () => {
      const isReady = type === 'attack' ? this.canAttack : this.canHeal;
      if (isReady && this.isSelected) {
        gsap.to(button.scale, { x: 1.15, y: 1.15, duration: 0.1 });
      }
    });

    button.on('pointerout', () => {
      gsap.to(button.scale, { x: 1, y: 1, duration: 0.1 });
    });

    return button;
  }

  private updateActionButtons(): void {
    // Update attack button
    if (this.attackButton) {
      const btn = this.attackButton as any;
      const size = btn.size;
      const isReady = this.canAttack;

      // Update background
      btn.bg.clear();
      btn.bg.roundRect(-size / 2, -size / 2, size, size, 4);
      btn.bg.fill({ color: COLORS.BG_TERTIARY, alpha: 0.9 });
      btn.bg.stroke({ color: isReady ? COLORS.DANGER : 0x444444, width: 2 });

      // Update icon or counter
      btn.iconContainer.clear();

      if (isReady || this.attackFrequency === 0) {
        // Show sword icon when ready
        const iconSize = size * 0.6;
        drawSwordIcon(btn.iconContainer, iconSize, isReady ? COLORS.DANGER : COLORS.TEXT_MUTED);
        btn.counterText.visible = false;
      } else {
        // Show counter when charging
        btn.counterText.text = `${this.attackCooldown}/${this.attackFrequency}`;
        btn.counterText.style.fill = COLORS.TEXT_PRIMARY; // Bright white for readability
        btn.counterText.visible = true;
      }

      this.attackButton.cursor = isReady && this.isSelected ? 'pointer' : 'default';
      this.attackButton.alpha = isReady ? 1 : 0.7;
    }

    // Update heal button
    if (this.healButton) {
      const btn = this.healButton as any;
      const size = btn.size;
      const isReady = this.canHeal;

      // Calculate heal charge progress
      const healReady = this.healFrequency === 0 || this.healCooldown >= this.healFrequency;

      // Update background
      btn.bg.clear();
      btn.bg.roundRect(-size / 2, -size / 2, size, size, 4);
      btn.bg.fill({ color: COLORS.BG_TERTIARY, alpha: 0.9 });
      btn.bg.stroke({ color: isReady ? COLORS.SUCCESS : 0x444444, width: 2 });

      // Update icon or counter
      btn.iconContainer.clear();

      if (healReady) {
        // Show heart icon when ready
        const iconSize = size * 0.6;
        drawHeartIcon(btn.iconContainer, iconSize, isReady ? COLORS.SUCCESS : COLORS.TEXT_MUTED);
        btn.counterText.visible = false;
      } else {
        // Show counter when charging
        btn.counterText.text = `${this.healCooldown}/${this.healFrequency}`;
        btn.counterText.style.fill = COLORS.TEXT_PRIMARY; // Bright white for readability
        btn.counterText.visible = true;
      }

      this.healButton.cursor = isReady && this.isSelected ? 'pointer' : 'default';
      this.healButton.alpha = isReady ? 1 : 0.7;
    }
  }

  /**
   * Update health bar display
   */
  updateHealth(currentHealth: number): void {
    this.options.warrior = {
      ...this.options.warrior,
      currentHealth,
    };
    this.drawHealthBar();
  }

  /**
   * Update readiness indicators (shown only when selected)
   * Also updates the aura (shown for all warriors when attack is ready)
   */
  setReadinessIndicators(
    canAttack: boolean,
    canHeal: boolean,
    attackCooldown?: number,
    healCooldown?: number
  ): void {
    this.canAttack = canAttack;
    this.canHeal = canHeal;
    if (attackCooldown !== undefined) {
      this.attackCooldown = attackCooldown;
    }
    if (healCooldown !== undefined) {
      this.healCooldown = healCooldown;
    }
    this.updateIndicatorVisibility();
    this.updateAuraVisibility();
    this.updateActionButtons();
  }

  private updateIndicatorVisibility(): void {
    // Hide old indicator container - we use action buttons now
    this.indicatorContainer.visible = false;
    this.indicatorAnimation?.kill();
    this.indicatorAnimation = null;

    // Show action buttons when selected
    this.actionButtonsContainer.visible = this.isSelected;
  }

  private updateAuraVisibility(): void {
    // Show electric aura when attack is ready (for all warriors, not just selected)
    if (this.canAttack && this.electricAura) {
      this.electricAura.visible = true;
      this.electricAura.startAnimation();
    } else if (this.electricAura) {
      this.electricAura.stopAnimation();
      this.electricAura.visible = false;
    }
  }

  private async loadSprite(): Promise<void> {
    const { warrior, tileSize } = this.options;
    const spriteSize = tileSize * 0.75;

    try {
      const texture = await Assets.load(warrior.definition.images.sprite);
      this.sprite = new Sprite({ texture });
      this.sprite.anchor.set(0.5);
      this.sprite.width = spriteSize;
      this.sprite.height = spriteSize;
      this.addChild(this.sprite);

      // Hide placeholder once sprite loads
      this.placeholder.visible = false;

      // Create electric aura now that sprite is loaded
      this.createElectricAura();

      // Update aura visibility based on current attack readiness
      this.updateAuraVisibility();
    } catch {
      // Keep placeholder visible
    }
  }

  /**
   * Create the electric aura effect based on sprite contour
   */
  private createElectricAura(): void {
    if (!this.sprite) return;

    const { tileSize } = this.options;
    const spriteSize = tileSize * 0.75;

    // Remove old aura if exists
    if (this.electricAura) {
      this.electricAura.dispose();
      this.removeChild(this.electricAura);
    }

    this.electricAura = new ElectricAura({
      sprite: this.sprite,
      size: spriteSize,
      color: COLORS.DANGER,
      particleCount: 20,
    });

    // Insert aura behind sprite but above health bar
    const spriteIndex = this.getChildIndex(this.sprite);
    this.addChildAt(this.electricAura, spriteIndex);
    this.electricAura.visible = false;
  }

  /**
   * Animate movement to a new position
   */
  animateMoveTo(targetX: number, targetY: number): Promise<void> {
    return new Promise((resolve) => {
      gsap.to(this, {
        x: targetX,
        y: targetY,
        duration: ANIMATION.MOVE_DURATION,
        ease: 'power2.out',
        onComplete: resolve,
      });
    });
  }

  /**
   * Set selection state
   */
  setSelected(selected: boolean): void {
    if (this.isSelected === selected) return;
    this.isSelected = selected;

    // Update indicator visibility based on selection
    this.updateIndicatorVisibility();

    // Start/stop pulse animation
    if (selected) {
      this.pulseAnimation = gsap.to(this.scale, {
        x: 1.08,
        y: 1.08,
        duration: ANIMATION.PULSE_DURATION,
        yoyo: true,
        repeat: -1,
        ease: 'power1.inOut',
      });
      // Counter-scale health bar and action buttons to keep them fixed
      const counterScale = 1 / 1.08;
      this.counterPulseAnimation = gsap.to(
        [this.healthBarContainer.scale, this.actionButtonsContainer.scale],
        {
          x: counterScale,
          y: counterScale,
          duration: ANIMATION.PULSE_DURATION,
          yoyo: true,
          repeat: -1,
          ease: 'power1.inOut',
        }
      );
    } else {
      this.pulseAnimation?.kill();
      this.pulseAnimation = null;
      this.counterPulseAnimation?.kill();
      this.counterPulseAnimation = null;
      gsap.to(this.scale, { x: 1, y: 1, duration: 0.2 });
      gsap.to([this.healthBarContainer.scale, this.actionButtonsContainer.scale], {
        x: 1,
        y: 1,
        duration: 0.2,
      });
    }
  }

  /**
   * Play damage effect with sparkles
   */
  playDamageEffect(): void {
    // Shake animation
    gsap.to(this, {
      x: this.x + 4,
      duration: 0.05,
      yoyo: true,
      repeat: 5,
    });

    // Sparkle particles
    const sparkleCount = 8;
    const { tileSize } = this.options;
    const sparkleRadius = tileSize * 0.4;

    for (let i = 0; i < sparkleCount; i++) {
      const sparkle = new Graphics();

      // Draw a 4-pointed star shape for sparkle
      const size = 3 + Math.random() * 4;
      sparkle.moveTo(0, -size);
      sparkle.lineTo(size * 0.3, -size * 0.3);
      sparkle.lineTo(size, 0);
      sparkle.lineTo(size * 0.3, size * 0.3);
      sparkle.lineTo(0, size);
      sparkle.lineTo(-size * 0.3, size * 0.3);
      sparkle.lineTo(-size, 0);
      sparkle.lineTo(-size * 0.3, -size * 0.3);
      sparkle.closePath();
      sparkle.fill({ color: Math.random() > 0.5 ? 0xffffff : 0xffff00 });

      // Random starting position around the warrior
      const angle = (i / sparkleCount) * Math.PI * 2 + Math.random() * 0.5;
      const startDist = sparkleRadius * 0.3;
      sparkle.x = Math.cos(angle) * startDist;
      sparkle.y = Math.sin(angle) * startDist;
      sparkle.rotation = Math.random() * Math.PI;

      this.addChild(sparkle);

      // Animate outward and fade
      const endDist = sparkleRadius + Math.random() * 20;
      gsap.to(sparkle, {
        x: Math.cos(angle) * endDist,
        y: Math.sin(angle) * endDist - 10,
        alpha: 0,
        rotation: sparkle.rotation + Math.PI,
        duration: 0.4 + Math.random() * 0.2,
        ease: 'power2.out',
        onComplete: () => sparkle.destroy(),
      });

      // Scale down as it moves
      gsap.to(sparkle.scale, {
        x: 0.3,
        y: 0.3,
        duration: 0.5,
        ease: 'power2.out',
      });
    }

    // Brief red flash
    const flash = new Graphics();
    flash.circle(0, 0, tileSize * 0.4);
    flash.fill({ color: COLORS.DANGER, alpha: 0.4 });
    this.addChild(flash);

    gsap.to(flash, {
      alpha: 0,
      duration: 0.2,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Play heal effect (green flash)
   */
  playHealEffect(): void {
    const flash = new Graphics();
    flash.circle(0, 0, this.options.tileSize * 0.4);
    flash.fill({ color: COLORS.SUCCESS, alpha: 0.5 });
    this.addChild(flash);

    gsap.to(flash, {
      alpha: 0,
      scale: 1.5,
      duration: 0.4,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Play attack burst - makes the electric aura explode outward and vanish
   */
  playAttackBurst(): void {
    const { tileSize } = this.options;

    // Create a dramatic burst effect even if electric aura isn't visible
    const burst = new Graphics();

    // Draw expanding ring
    const burstRadius = tileSize * 0.5;
    burst.circle(0, 0, burstRadius);
    burst.stroke({ color: COLORS.DANGER, width: 4, alpha: 1 });
    burst.circle(0, 0, burstRadius * 0.7);
    burst.stroke({ color: 0xffffff, width: 2, alpha: 0.8 });

    this.addChild(burst);

    // Animate burst expanding outward
    gsap.to(burst.scale, {
      x: 3,
      y: 3,
      duration: 0.5,
      ease: 'power2.out',
    });

    gsap.to(burst, {
      alpha: 0,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => burst.destroy(),
    });

    // Also animate the electric aura if present
    if (this.electricAura && this.electricAura.visible) {
      this.electricAura.stopAnimation();

      gsap.to(this.electricAura.scale, {
        x: 2.5,
        y: 2.5,
        duration: 0.5,
        ease: 'power2.out',
      });

      gsap.to(this.electricAura, {
        alpha: 0,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          if (this.electricAura) {
            this.electricAura.scale.set(1);
            this.electricAura.alpha = 1;
            this.electricAura.visible = false;
          }
        },
      });
    }

    // Add flash effect
    const flash = new Graphics();
    flash.circle(0, 0, tileSize * 0.4);
    flash.fill({ color: COLORS.DANGER, alpha: 0.6 });
    this.addChild(flash);

    gsap.to(flash, {
      alpha: 0,
      duration: 0.3,
      delay: 0.1,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Play heart effect - heart grows from center and fades out
   */
  playHeartEffect(): void {
    const { tileSize, isPlayer2 } = this.options;
    const heartSize = tileSize * 0.4;

    // Create heart shape
    const heart = new Graphics();
    const half = heartSize / 2;

    // Draw heart using bezier curves
    heart.moveTo(0, half * 0.3);
    heart.bezierCurveTo(-half, -half * 0.5, -half, -half * 0.8, 0, -half * 0.4);
    heart.bezierCurveTo(half, -half * 0.8, half, -half * 0.5, 0, half * 0.3);
    heart.fill({ color: COLORS.SUCCESS });

    // Add a white highlight
    const highlight = new Graphics();
    highlight.circle(-half * 0.25, -half * 0.3, half * 0.15);
    highlight.fill({ color: 0xffffff, alpha: 0.7 });
    heart.addChild(highlight);

    // Start at center of warrior
    heart.x = 0;
    heart.y = 0;
    heart.alpha = 1;
    heart.scale.set(0.5);

    this.addChild(heart);

    // Counter-rotate if player 2 so heart stays upright
    if (isPlayer2) {
      heart.rotation = -Math.PI;
    }

    // Animate: grow big and fade out simultaneously
    gsap.to(heart.scale, {
      x: 4,
      y: 4,
      duration: 0.6,
      ease: 'power2.out',
    });

    gsap.to(heart, {
      alpha: 0,
      duration: 0.6,
      ease: 'power1.in',
      onComplete: () => heart.destroy(),
    });
  }

  /**
   * Play defeat animation (scale down, spin, fade out)
   */
  playDefeatAnimation(): Promise<void> {
    return new Promise((resolve) => {
      // Stop any ongoing animations
      this.pulseAnimation?.kill();

      // Create explosion particles around the sprite
      const particleCount = 12;
      const particles: Graphics[] = [];

      for (let i = 0; i < particleCount; i++) {
        const particle = new Graphics();
        const size = 3 + Math.random() * 5;
        particle.circle(0, 0, size);
        particle.fill({
          color: Math.random() > 0.5 ? COLORS.DANGER : COLORS.WARNING,
        });
        this.parent?.addChild(particle);
        particle.x = this.x;
        particle.y = this.y;
        particles.push(particle);

        const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
        const distance = 40 + Math.random() * 30;

        gsap.to(particle, {
          x: this.x + Math.cos(angle) * distance,
          y: this.y + Math.sin(angle) * distance - 20,
          alpha: 0,
          duration: 0.5,
          ease: 'power2.out',
          onComplete: () => particle.destroy(),
        });
      }

      // Flash white briefly
      const flash = new Graphics();
      flash.circle(0, 0, this.options.tileSize * 0.5);
      flash.fill({ color: 0xffffff, alpha: 0.8 });
      this.addChild(flash);

      gsap.to(flash, {
        alpha: 0,
        scale: 2,
        duration: 0.3,
        onComplete: () => flash.destroy(),
      });

      // Animate the sprite: shrink, spin, fade
      gsap.to(this.scale, {
        x: 0,
        y: 0,
        duration: 0.4,
        ease: 'back.in(2)',
      });

      gsap.to(this, {
        rotation: this.rotation + Math.PI * 2,
        alpha: 0,
        duration: 0.4,
        ease: 'power2.in',
        onComplete: resolve,
      });
    });
  }

  /**
   * Update the tile size
   */
  setTileSize(tileSize: number): void {
    this.options.tileSize = tileSize;
    this.draw();
    this.drawHealthBar();
    this.drawIndicators();
    this.createActionButtons();

    if (this.sprite) {
      const spriteSize = tileSize * 0.75;
      this.sprite.width = spriteSize;
      this.sprite.height = spriteSize;

      // Update electric aura with new size
      if (this.electricAura) {
        this.electricAura.updateSprite(this.sprite, spriteSize);
      }
    }
  }

  /**
   * Get the warrior instance
   */
  getWarrior(): WarriorInstance {
    return this.options.warrior;
  }

  dispose(): void {
    this.pulseAnimation?.kill();
    this.counterPulseAnimation?.kill();
    this.indicatorAnimation?.kill();
    this.electricAura?.dispose();
    this.destroy({ children: true });
  }
}
